import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Plus, Edit, Trash2, Upload, Download, Save, X, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export function MasterShell({ title, subtitle, onAdd, onTemplate, onImport, children, importTestid = "import-btn", templateTestid = "template-btn", addTestid = "add-btn", canManage }) {
  const fileRef = useRef(null);
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Masters</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={onTemplate} data-testid={templateTestid} className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
            <Download size={14} /> Template
          </button>
          {canManage && (
            <>
              <input type="file" accept=".xlsx" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = ""; } }} />
              <button onClick={() => fileRef.current?.click()} data-testid={importTestid} className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
                <Upload size={14} /> Import Excel
              </button>
              {onAdd && (
                <button onClick={onAdd} data-testid={addTestid} className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold">
                  <Plus size={14} /> Add
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

async function downloadTemplate(path, filename) {
  const token = localStorage.getItem("pms_token");
  const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function IntenderMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, name}
  const [q, setQ] = useState("");

  const load = () => api.get("/intenders").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => list.filter((i) => !q.trim() || (i.name || "").toLowerCase().includes(q.trim().toLowerCase())),
    [list, q]
  );

  const save = async () => {
    if (!edit?.name?.trim()) return toast.error("Name required");
    if (edit.id) await api.put(`/intenders/${edit.id}`, { name: edit.name });
    else await api.post("/intenders", { name: edit.name });
    setEdit(null);
    toast.success("Saved");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this intender?")) return;
    await api.delete(`/intenders/${id}`);
    toast.success("Deleted");
    load();
  };

  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/intenders/import", fd);
    toast.success(`Imported ${data.inserted} intenders`);
    load();
  };

  return (
    <MasterShell
      title="Intender Master"
      subtitle="Manage employees who can raise indents."
      onAdd={canManage ? () => setEdit({ name: "" }) : null}
      onTemplate={() => downloadTemplate("/intenders/template", "intender_template.xlsx")}
      onImport={onImport}
      canManage={canManage}
    >
      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-end gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Intender Name</div>
            <input data-testid="intender-name-input" autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <button onClick={save} data-testid="intender-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
          <button onClick={() => setEdit(null)} className="border border-slate-300 px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><X size={14} /> Cancel</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">All Intenders</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="intender-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search intender…"
              className="pl-9 pr-3 py-1.5 border border-slate-300 rounded-md text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <th className="text-left px-6 py-3">Name</th>
            <th className="text-left px-6 py-3">Created</th>
            {canManage && <th className="text-right px-6 py-3">Actions</th>}
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="3" className="text-center py-12 text-slate-400">{list.length === 0 ? "No intenders" : "No matches for your search"}</td></tr>}
            {filtered.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-3 font-medium">{i.name}</td>
                <td className="px-6 py-3 font-mono text-xs text-slate-500">{i.created_at?.slice(0, 10)}</td>
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit({ id: i.id, name: i.name })} className="p-1.5 hover:bg-slate-100 rounded text-slate-600 mr-1"><Edit size={14} /></button>
                    <button onClick={() => del(i.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MasterShell>
  );
}

export function ConsumableMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [fields, setFields] = useState([]);
  const [edit, setEdit] = useState(null);
  const [newField, setNewField] = useState("");

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/consumables"), api.get("/consumables/fields")]);
    setList(a.data);
    setFields(b.data.fields);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit?.code?.trim() || !edit?.type_of_purchase?.trim()) return toast.error("Code and type required");
    if (edit.id) await api.put(`/consumables/${edit.id}`, edit);
    else await api.post("/consumables", edit);
    setEdit(null);
    toast.success("Saved");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/consumables/${id}`);
    load();
  };

  const addField = async () => {
    if (!newField.trim()) return;
    await api.post("/consumables/fields", { field_name: newField.trim() });
    setNewField("");
    load();
  };
  const delField = async (name) => {
    await api.delete(`/consumables/fields/${encodeURIComponent(name)}`);
    load();
  };

  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/consumables/import", fd);
    toast.success(`Imported ${data.inserted} consumables`);
    load();
  };

  return (
    <MasterShell
      title="Consumable Master"
      subtitle="Type of purchase + T-codes. Add custom fields and re-download template."
      onAdd={canManage ? () => setEdit({ type_of_purchase: "", code: "", extra: {} }) : null}
      onTemplate={() => downloadTemplate("/consumables/template", "consumable_template.xlsx")}
      onImport={onImport}
      canManage={canManage}
    >
      {/* Dynamic fields admin panel */}
      {canManage && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-slate-900 mb-2">Custom Fields</div>
          <div className="text-xs text-slate-500 mb-3">Add custom columns. Excel template updates automatically.</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {fields.length === 0 && <span className="text-xs text-slate-400">No custom fields yet.</span>}
            {fields.map((f) => (
              <span key={f} className="text-xs bg-slate-100 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                {f}
                <button onClick={() => delField(f)} className="text-rose-600 hover:text-rose-800"><X size={10} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="consumable-field-input" placeholder="e.g. Brand, Specification" value={newField} onChange={(e) => setNewField(e.target.value)} className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm" />
            <button onClick={addField} data-testid="consumable-add-field-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Plus size={14} /> Add Field</button>
          </div>
        </div>
      )}

      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 gap-3">
          <div>
            <Lbl>Type of Purchase</Lbl>
            <input data-testid="consumable-type-input" value={edit.type_of_purchase} onChange={(e) => setEdit({ ...edit, type_of_purchase: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <Lbl>Code (e.g. T-01)</Lbl>
            <input data-testid="consumable-code-input" value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
          </div>
          {fields.map((f) => (
            <div key={f}>
              <Lbl>{f}</Lbl>
              <input value={edit.extra?.[f] || ""} onChange={(e) => setEdit({ ...edit, extra: { ...(edit.extra || {}), [f]: e.target.value } })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </div>
          ))}
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setEdit(null)} className="border border-slate-300 px-4 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={save} data-testid="consumable-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <th className="text-left px-6 py-3">Code</th>
            <th className="text-left px-6 py-3">Type of Purchase</th>
            {fields.map((f) => <th key={f} className="text-left px-6 py-3">{f}</th>)}
            {canManage && <th className="text-right px-6 py-3">Actions</th>}
          </tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={3 + fields.length} className="text-center py-12 text-slate-400">No consumables</td></tr>}
            {list.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-3 font-mono font-semibold">{c.code}</td>
                <td className="px-6 py-3">{c.type_of_purchase}</td>
                {fields.map((f) => <td key={f} className="px-6 py-3 text-slate-600">{c.extra?.[f] || "—"}</td>)}
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit({ ...c, extra: c.extra || {} })} className="p-1.5 hover:bg-slate-100 rounded text-slate-600 mr-1"><Edit size={14} /></button>
                    <button onClick={() => del(c.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MasterShell>
  );
}

export function SupplierMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/suppliers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((sp) =>
      [sp.supplier_code, sp.supplier_name, sp.gst_number, sp.address]
        .some((v) => (v || "").toLowerCase().includes(s))
    );
  }, [list, q]);

  const save = async () => {
    if (!edit.supplier_name?.trim()) return toast.error("Supplier Name is required");
    try {
      if (edit.id) await api.put(`/suppliers/${edit.id}`, edit);
      else await api.post("/suppliers", edit);
      setEdit(null);
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/suppliers/${id}`);
    load();
  };
  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/suppliers/import", fd);
    toast.success(`Imported ${data.inserted}, skipped ${data.skipped} duplicates`);
    load();
  };

  return (
    <MasterShell
      title="Supplier Master"
      subtitle="Maintain supplier directory with unique codes."
      onAdd={canManage ? () => setEdit({ supplier_name: "", supplier_code: "", gst_number: "", address: "", details: "" }) : null}
      onTemplate={() => downloadTemplate("/suppliers/template", "supplier_template.xlsx")}
      onImport={onImport}
      canManage={canManage}
    >
      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 gap-3">
          <div><Lbl>Supplier Name</Lbl><input data-testid="supplier-name-input" value={edit.supplier_name} onChange={(e) => setEdit({ ...edit, supplier_name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" /></div>
          <div>
            <Lbl>Supplier Code {edit.id ? "" : "(auto)"}</Lbl>
            <input
              data-testid="supplier-code-input"
              value={edit.supplier_code || ""}
              readOnly
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono bg-slate-50 text-slate-600"
              placeholder={edit.id ? "" : "Will auto-generate on save (SUP-###)"}
            />
          </div>
          <div><Lbl>GST Number</Lbl><input value={edit.gst_number} onChange={(e) => setEdit({ ...edit, gst_number: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" /></div>
          <div><Lbl>Address</Lbl><input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" /></div>
          <div className="col-span-2"><Lbl>Details</Lbl><textarea value={edit.details} onChange={(e) => setEdit({ ...edit, details: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[60px]" /></div>
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setEdit(null)} className="border border-slate-300 px-4 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={save} data-testid="supplier-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">All Suppliers</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="supplier-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code, name, GST or address…"
              className="pl-9 pr-3 py-1.5 border border-slate-300 rounded-md text-sm w-80 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <th className="text-left px-6 py-3">Code</th>
            <th className="text-left px-6 py-3">Name</th>
            <th className="text-left px-6 py-3">GST</th>
            <th className="text-left px-6 py-3">Address</th>
            {canManage && <th className="text-right px-6 py-3">Actions</th>}
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="5" className="text-center py-12 text-slate-400">{list.length === 0 ? "No suppliers" : "No matches for your search"}</td></tr>}
            {filtered.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-3 font-mono font-semibold">{s.supplier_code}</td>
                <td className="px-6 py-3">{s.supplier_name}</td>
                <td className="px-6 py-3 font-mono text-xs">{s.gst_number || "—"}</td>
                <td className="px-6 py-3 text-slate-600">{s.address || "—"}</td>
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit(s)} className="p-1.5 hover:bg-slate-100 rounded text-slate-600 mr-1"><Edit size={14} /></button>
                    <button onClick={() => del(s.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MasterShell>
  );
}

const Lbl = ({ children }) => (<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>);
